import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useEffectFlag } from "./effects";
import { rollHrDistance } from "./utils";

// Home plate's position inside the diamond container (matches BASE_COORDS.home in
// constants.js — kept here as a literal so we don't take a cross-module dep on
// what's logically a layout constant).
const HOME_X_PCT = 50;
const HOME_Y_PCT = 75;

// Where the counter parks vertically — just below the scoreboard nav (≈48px tall).
// The ball flies past this; the counter sticks here so the final number is readable.
const COUNTER_PARK_PX = 60;

// Horizontal gap between ball and counter on wider screens. Below STACK_BREAKPOINT_PX
// the counter stacks vertically below the parked y instead so it can't get clipped
// or overlap the top-right action buttons (Fast / HR / WiFi badge).
const COUNTER_GAP_PX = 16;
const COUNTER_STACK_OFFSET_PX = 22;
const STACK_BREAKPOINT_PX = 640;

// Distance band/bias documentation (kept here as the source of truth even after the
// math moved to utils.js#rollHrDistance):
//
//   power 7  (a "barely cleared the wall" HR): floor 412 ft, heavy low bias —
//             50% of HRs in 412–430 range, only top 2.5% reach 510+.
//   power 10 (solid contact, weak fielding): floor ~433 ft, moderate bias.
//   power 14 (monster swing): floor ~462 ft, bias flips toward upper end —
//             most rolls land in the 480–517 band.
//
// Power is clamped to [7, 14] so out-of-band values can't break the math.

/**
 * Cinematic HR ball: a white circle that flies from home plate up off the screen,
 * pulsing 3x at the peak of its arc. Travels above the diamond / phase panels but
 * behind the top scoreboard nav (z-[500]). A small distance counter
 * rides up alongside the ball, parks just below the nav, finishes ticking up to a
 * random "feet" value for a suspense beat, then fades.
 *
 * Origin is computed at trigger time from the live diamond container's bounding
 * rect, so a window resize or layout shift between HRs is handled correctly. On
 * narrow screens the counter stacks below the parked spot to avoid clashing with
 * the top-right action buttons.
 *
 * Disabled via the `HR_BALL` effect flag:
 *   window.__JB_EFFECTS__.HR_BALL = false
 */
export default function HRBallEffect({ triggerKey, power = null, seed = null }) {
  const enabled = useEffectFlag("HR_BALL");
  // Per-trigger origin in viewport coords + this HR's distance target. Recomputed
  // each fire so resizes don't strand the ball at a stale position and so each
  // HR rolls a fresh distance.
  const [origin, setOrigin] = useState(null);
  const [displayDistance, setDisplayDistance] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    if (triggerKey == null || triggerKey === 0) return; // 0 = initial mount, never fired
    const el = document.querySelector('[data-testid="diamond-field"]');
    if (!el) return;
    const r = el.getBoundingClientRect();
    setOrigin({
      key: triggerKey,
      x: r.left + (r.width * HOME_X_PCT) / 100,
      y: r.top + (r.height * HOME_Y_PCT) / 100,
      target: rollHrDistance(power, seed),
      isNarrow: typeof window !== "undefined" && window.innerWidth < STACK_BREAKPOINT_PX,
    });
  }, [triggerKey, enabled, power, seed]);

  // Distance counter ramp: 0 → target with quartic ease-out so the number rips
  // up fast at the start and crawls into the final value — that crawl is the
  // suspense beat. Runs ~2.2s, finishing while the counter is still parked
  // and visible (counter total visible ~3.0s).
  useEffect(() => {
    if (!origin) return;
    setDisplayDistance(0);
    const start = Date.now();
    const durationMs = 2200;
    const id = setInterval(() => {
      const t = Math.min(1, (Date.now() - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 4); // quartic ease-out
      setDisplayDistance(Math.floor(origin.target * eased));
      if (t >= 1) clearInterval(id);
    }, 30);
    return () => clearInterval(id);
  }, [origin?.key, origin]);

  if (!enabled || !origin) return null;

  const BALL_PX = 12;
  const cx = origin.x - BALL_PX / 2;
  const cy = origin.y - BALL_PX / 2;

  // Counter horizontal placement: beside the ball on wide screens, directly under
  // the parked ball position on narrow screens (stack below).
  const counterLeft = origin.isNarrow ? cx : cx + BALL_PX + COUNTER_GAP_PX;
  const counterParkY = origin.isNarrow
    ? COUNTER_PARK_PX + COUNTER_STACK_OFFSET_PX
    : COUNTER_PARK_PX;

  return (
    <div className="pointer-events-none fixed inset-0 z-[400] overflow-hidden">
      <AnimatePresence>
        {/* The ball itself. Quartic-ease bezier (smoother than the default easeOut)
            and a small x-drift sequence give it an organic curve instead of a
            straight vertical line — reads as a hit ball, not a launching rocket. */}
        <motion.div
          key={`ball-${origin.key}`}
          initial={{ y: cy, x: 0, scale: 0.6, opacity: 0 }}
          animate={{
            y: 0,                             // ends at the top edge of the viewport
            x: [0, -6, 4, -2, 0],             // subtle drift through the path
            scale: [0.6, 3.0, 1.5, 0.5],      // small → peak (3x base) → settle → tiny dot
            opacity: [0, 1, 1, 0.7, 0],
          }}
          exit={{ opacity: 0 }}
          transition={{
            duration: 1.8,
            ease: [0.25, 0.46, 0.45, 0.94],   // cubic-bezier ease-out-quad
            x:       { times: [0, 0.25, 0.55, 0.8, 1], duration: 1.8 },
            scale:   { times: [0, 0.45, 0.7, 1],       duration: 1.8 },
            opacity: { times: [0, 0.1, 0.5, 0.85, 1],  duration: 1.8 },
          }}
          style={{ position: "absolute", top: 0, left: cx }}
          className="w-3 h-3 rounded-full bg-white shadow-[0_0_18px_rgba(255,255,255,0.85)]"
        />

        {/* Distance counter — rides up beside the ball, parks just below the nav,
            holds while the number finishes ticking, then fades. The y-keyframe
            lands at counterParkY at ~40% of the timeline (≈1.2s) and stays there
            for the rest of the 3.0s window so the suspense beat plays out at the
            parked spot. On narrow screens the parked y drops a bit and x sits
            under the ball (stacked) instead of beside it. */}
        <motion.div
          key={`counter-${origin.key}`}
          initial={{ y: cy, x: 0, opacity: 0 }}
          animate={{
            y:       [cy, counterParkY, counterParkY, counterParkY],
            x:       [0, -6, 4, -2, 0],
            opacity: [0, 1, 1, 0],
          }}
          transition={{
            duration: 3.0,
            ease: [0.25, 0.46, 0.45, 0.94],
            y:       { times: [0, 0.4, 0.85, 1],            duration: 3.0 },
            x:       { times: [0, 0.15, 0.33, 0.48, 0.6],   duration: 3.0 },
            opacity: { times: [0, 0.08, 0.85, 1],           duration: 3.0 },
          }}
          style={{ position: "absolute", top: 0, left: counterLeft }}
          className="px-1.5 py-0.5 bg-black/80 rounded font-mono font-bold text-amber-300 text-[10px] tabular-nums whitespace-nowrap"
        >
          {displayDistance} ft
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
